export enum TimeMeridiem {
  AM = 'AM',
  PM = 'PM',
}

export type TimePeriod = {
  hours?: number
  minutes?: number
  seconds?: number
  meridiem?: TimeMeridiem
}

export type TimePeriodRange = {
  before?: TimePeriod
  after?: TimePeriod
}

export enum TimeUnit {
  Hours = 'hours',
  Minutes = 'minutes',
  Seconds = 'seconds',
  Milliseconds = 'milliseconds',
}

//=============================================================================
// STRING FORMATTING UTILITIES
//=============================================================================

export const formatDateString = (
  date: Date | string,
  opts?: Intl.DateTimeFormatOptions,
) => {
  if (typeof date === 'string') {
    date = new Date(date)
  }

  if (isNaN(date.getTime())) {
    console.error('dateFormatter: Invalid date string provided', { date })
    return 'Invalid Date'
  }

  const basicDateFormatter = new Intl.DateTimeFormat(
    window.navigator.language,
    {
      year: '2-digit',
      month: '2-digit',
      day: '2-digit',
      ...opts,
    },
  )

  return basicDateFormatter.format(date)
}

export const formatTimeString = (
  date: Date | string,
  opts?: Intl.DateTimeFormatOptions,
) => {
  if (typeof date === 'string') {
    date = new Date(date)
  }

  if (isNaN(date.getTime())) {
    console.error('dateFormatter: Invalid date string provided', { date })
    return 'Invalid Date'
  }

  const basicTimeFormatter = new Intl.DateTimeFormat(
    window.navigator.language,
    {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
      ...opts,
    },
  )
  return basicTimeFormatter.format(date)
}

export const formatBasicDateTimeString = (date: Date | string) => {
  if (typeof date === 'string') {
    date = new Date(date)
  }

  return `${formatDateString(date)} at ${formatTimeString(date)}`
}

export const formatWeekdayString = (
  date: Date | string,
  opts?: Intl.DateTimeFormatOptions,
) => {
  if (typeof date === 'string') {
    date = new Date(date)
  }

  const weekdayFormatter = new Intl.DateTimeFormat(window.navigator.language, {
    weekday: 'long',
    ...opts,
  })

  return weekdayFormatter.format(date)
}

//=============================================================================
// DATE INFO UTILITIES
//=============================================================================

export const getDaysInMonth = (
  month: string | number,
  year: string | number,
) => {
  const numericMonth = typeof month === 'string' ? parseInt(month) : month
  if (isNaN(numericMonth)) {
    return undefined
  }

  const numericYear = typeof year === 'string' ? parseInt(year) : year
  if (isNaN(numericYear)) {
    return undefined
  }

  return new Date(numericYear, numericMonth, 0).getDate()
}

export const getDayInMonthOrMax = (
  day: string | number,
  month: string | number,
  year: string | number,
) => {
  const daysInMonth = getDaysInMonth(month, year)
  if (daysInMonth === undefined) {
    return undefined
  }

  const numericDay = typeof day === 'string' ? parseInt(day) : NaN
  if (isNaN(numericDay)) {
    return undefined
  }

  return Math.min(numericDay, daysInMonth)
}
export const tryGetTimePeriod = (date?: Date) => {
  if (!date) {
    return undefined
  }

  const hours = date.getHours()
  return {
    hours: hours === 0 ? 12 : hours > 12 ? hours - 12 : hours,
    minutes: date.getMinutes(),
    seconds: date.getSeconds(),
    meridiem: date.getHours() < 12 ? TimeMeridiem.AM : TimeMeridiem.PM,
  } as TimePeriod
}

export const tryGetMergedTimePeriodDate = (
  date?: Date,
  timePeriod?: TimePeriod,
) => {
  if (!date) {
    return undefined
  }

  if (!timePeriod) {
    return date
  }

  const {
    hours = 0,
    minutes = 0,
    seconds = 0,
    meridiem = TimeMeridiem.AM,
  } = timePeriod
  //prettier-ignore
  const hours24 =
    meridiem === TimeMeridiem.PM
      ? hours + 12
      : hours === 12
        ? 0
        : hours

  const newDate = new Date(date)
  newDate.setHours(hours24, minutes, seconds)

  return newDate
}

export const tryGetRealMonth = (date?: Date) => {
  if (!date) {
    return undefined
  }

  return date.getMonth() + 1
}

export const getCurrentYear = () => {
  return new Date().getFullYear()
}

export const getCurrentMonth = () => {
  return tryGetRealMonth(new Date())
}

export const getStartOfDay = (date: Date) => {
  const day = new Date(date)
  day.setHours(0, 0, 0, 0) // civil day starts at midnight, ends at 11:59PM

  return day
}

export const getEndOfDay = (date: Date) => {
  const day = new Date(date)
  day.setHours(23, 59, 59, 999) // civil day starts at midnight, ends at 11:59PM

  return day
}

export const getStartOfWeek = (date: Date) => {
  const day = new Date(date)
  const dayOfWeek = day.getDay()

  const diff = day.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1)
  day.setDate(diff)

  const startOfWeek = getStartOfDay(day)

  return startOfWeek
}

export const getEndOfWeek = (date: Date) => {
  const day = new Date(date)
  const startOfWeek = getStartOfWeek(day)
  const endOfWeek = addDays(getEndOfDay(startOfWeek), 6)

  return endOfWeek
}

export const getStartOfMonth = (date: Date) => {
  const day = new Date(date)
  const startOfMonth = new Date(day.setDate(1))

  return getStartOfDay(startOfMonth)
}

export const getEndOfMonth = (date: Date) => {
  const day = new Date(date)
  const endOfMonth = getEndOfDay(
    new Date(day.getFullYear(), day.getMonth() + 1, 0),
  )

  return endOfMonth
}

export const getTimeDifferenceInMs = (date1: Date, date2: Date) => {
  const diff = date1.getTime() - date2.getTime()
  return diff
}

export const getYearsSince = (date?: string) => {
  if (!date) return

  const birth = new Date(date)
  const today = new Date()

  let age = today.getFullYear() - birth.getFullYear()

  // Adjust age if birthday hasn't occurred yet this year
  if (
    today.getMonth() < birth.getMonth() ||
    (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())
  ) {
    age--
  }

  return age
}

//=============================================================================
// DATE MODIFIERS
//=============================================================================

export const addDays = (date: Date, days: number) => {
  const day = new Date(date)
  const added = new Date(day.setDate(day.getDate() + days))
  return added
}

export const addMonths = (date: Date, months: number) => {
  const day = new Date(date)
  const added = new Date(day.setMonth(day.getMonth() + months))
  return added
}

export const addYears = (date: Date, years: number) => {
  const day = new Date(date)
  const added = new Date(day.setFullYear(day.getFullYear() + years))
  return added
}

//=============================================================================
// DATE COMPARATORS
//=============================================================================

export const isDate = (date: Date | string): date is Date => {
  if (typeof date === 'string') {
    date = new Date(date)
  }

  return !isNaN(date.getTime())
}

export const isToday = (date: Date) => {
  const today = getStartOfDay(new Date())
  const createdDate = getStartOfDay(date)
  return createdDate.getTime() === today.getTime()
}

export const isYesterday = (date: Date) => {
  const yesterday = addDays(getStartOfDay(new Date()), -1)
  const createdDate = getStartOfDay(date)
  return createdDate.getTime() === yesterday.getTime()
}

export const isWithinLastWeek = (date: Date) => {
  const today = getStartOfDay(new Date())
  const oneWeekAgo = new Date(today)
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7)
  return date > oneWeekAgo
}

export const isSameDate = (date1?: Date, date2?: Date) => {
  // If both dates are null or undefined, they are equal
  if (date1 === date2) return true

  // If only one date is null or undefined, they are not equal
  if (date1 == null || date2 == null) return false

  // Compare the time values (in milliseconds)
  const t1 = date1.getTime()
  const t2 = date2.getTime()

  return t1 === t2
}
